import { Link } from "react-router-dom";
import { useContent } from "@/contexts/ContentContext";
import Marquee from "@/components/Marquee";
import SizeCarousel from "@/components/SizeCarousel";
import { ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const SIZES = [
  { size: "10", desc: "For smaller pilings on residential docks and slips." },
  { size: "12", desc: "The most popular size — fits standard marina pilings." },
  { size: "14", desc: "Heavy-duty applications and oversized pilings." },
];

export default function Home() {
  const { content } = useContent();
  useSEO({
    title: "Secure Stay Marine — Pile Glide Pro Marine Pile Hoops",
    description: "USA-made marine pile hoops. Pile Glide Pro reduces wear, never rusts, withstands harsh conditions with the best UV inhibitor on the market. Sizes 10\", 12\", 14\".",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b-2 border-[#091526]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1
              className="font-display uppercase mb-6"
              data-testid="hero-brand"
            >
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] whitespace-nowrap">
                Pile Glide Pro
              </span>
            </h1>
            <h2
              className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[0.95] uppercase mb-8 text-[#1E5BB8]"
              data-testid="hero-title"
            >
              {content.hero_title}
            </h2>
            <p className="text-base md:text-lg max-w-xl text-[#091526]/80 mb-10 leading-relaxed">
              {content.hero_subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-3 bg-[#1E5BB8] text-white font-bold uppercase text-sm tracking-[0.2em] px-7 py-4 border-2 border-[#091526] hard-shadow hard-shadow-hover"
              >
                {content.hero_cta_label}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/product"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 bg-transparent text-[#091526] font-bold uppercase text-sm tracking-[0.2em] px-7 py-4 border-2 border-[#091526] hover:bg-[#091526] hover:text-[#F4F4F5] transition-colors"
              >
                View Specs
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border-2 border-[#091526] bg-white hard-shadow">
              <img
                src="/images/hero.jpg"
                alt="Pile Glide Pro on a marine piling"
                className="w-full h-[420px] md:h-[520px] object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURES */}
      <section className="border-b-2 border-[#091526]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-none mb-16 max-w-2xl">
            Built for the worst the water can throw at it.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[#091526]">
            <Feature title={content.feature_1_title} body={content.feature_1_body} />
            <Feature title={content.feature_2_title} body={content.feature_2_body} divider />
            <Feature title={content.feature_3_title} body={content.feature_3_body} divider />
          </div>
        </div>
      </section>

      {/* SIZES */}
      <section className="border-b-2 border-[#091526] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-none mb-6">
              Pile Guide Attachments
            </h2>
            <p className="text-base text-[#091526]/80 mb-6 leading-relaxed">{content.sizes_note}</p>

            <div className="bg-[#1E5BB8] text-white border-2 border-[#091526] px-5 py-4 mb-8" data-testid="sizes-fit-note">
              <p className="font-mono text-sm leading-relaxed">
                {content.sizes_fit_note || `Select a Pile Glide Pro at least 1" larger than your Pile's diameter for proper fit.`}
              </p>
            </div>

            <div className="border-2 border-[#091526]">
              {SIZES.map((s, i) => (
                <div
                  key={s.size}
                  className={`px-6 py-5 ${i < SIZES.length - 1 ? "border-b-2 border-[#091526]" : ""}`}
                  data-testid={`size-row-${s.size}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-display text-3xl">{s.size}″</span>
                    <Link
                      to="/contact"
                      className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-[#1E5BB8] hover:text-[#091526]"
                    >
                      Inquire →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <SizeCarousel testIdPrefix="home-size-carousel" />
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ title, body, divider }) {
  return (
    <div className={`p-8 md:p-10 bg-white ${divider ? "md:border-l-2 md:border-[#091526]" : ""}`}>
      <h3 className="font-display text-3xl uppercase mb-4">{title}</h3>
      <p className="text-sm text-[#091526]/75 leading-relaxed">{body}</p>
    </div>
  );
}
